import React, { useState, useEffect } from "react";
import { Button, Space, Spin, Select, Pagination } from "antd";
import { ToastContainer, toast } from "react-toastify";
import BaseTable from "../components/BaseTable";
import PageSnipper from "../components/PageSnipper";
import CustomButton from "../components/CustomButton";
import { CalendarPlus, Frown } from "lucide-react";

import { getReqParamheader, deleteReqParamheader } from "../services/apiCall";
import { API_URL_CONSTANT } from "../constants/apiConstant";
import { useLocation, useNavigate } from "react-router-dom";
import { getItem, setItem } from "../utils/storage";

function EventsHome() {
  const [eventList, setEventList] = useState([]);
  const [eventListId, setEventListId] = useState([]);
  const [loading, setLoading] = useState(false);
  const [org, setOrg] = useState("");
  const [user, setUser] = useState("");
  const path = useLocation().pathname;
  const navigate = useNavigate();
  const [currentDays, setCurrentDays] = useState(7);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalEvents, setTotalEvents] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Day options for dropdown
  const dayOptions = [
    { label: "7 days", value: 7 },
    { label: "14 days", value: 14 },
    { label: "21 days", value: 21 },
    { label: "28 days", value: 28 },
  ];

  useEffect(() => {
    let userid = getItem("user_id");
    let orgid = path.split("/")[2];
    if (userid && orgid) {
      setItem("org_id", orgid);
      setOrg(orgid);
      setUser(userid);
    }
  }, []);

  useEffect(() => {
    if (user && org) {
      getEventData();
    }
  }, [user, org, currentDays, currentPage, pageSize]);

  const getEventData = () => {
    setLoading(true);

    let url = `${API_URL_CONSTANT.baseUrl}/events/${org}/${user}?eventDays=${currentDays}&page=${currentPage}&limit=${pageSize}`;
    console.log("API URL:", url);
    console.log("Pagination params:", { currentDays, currentPage, pageSize });

    getReqParamheader(url)
      .then((res) => {
        if (res?.status) {
          console.log("get event list response", res?.data);
          setEventList(res?.data?.data);
          setEventListId(res?.data?.data); // Updated to use the paginated data

          // Set pagination info from backend response
          if (res?.data?.pagination) {
            setTotalEvents(res.data.pagination.totalEvents);
            setTotalPages(res.data.pagination.totalPages);
          }

          setLoading(false);
        } else {
          console.log("response err", res?.message);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.log("err", err);
        toast.error(err?.response?.data?.message);
        setLoading(false);
      });
  };

  const handleDelete = (id, index) => {
    let a = window.confirm("Are you sure you want to delete this event?");
    if (a) {
      setLoading(true);
      let url = `${API_URL_CONSTANT.baseUrl}/delete_events/${id}/${eventListId[index]?._id}/${org}/${user}`;
      deleteReqParamheader(url)
        .then((res) => {
          if (res?.status) {
            console.log("delete event by id response", res?.data);
            setLoading(false);
            // Reset to first page after deletion to avoid empty pages
            if (eventList.length === 1 && currentPage > 1) {
              setCurrentPage(currentPage - 1);
            } else {
              getEventData();
            }
            toast.success(res?.data?.message);
          } else {
            console.log("response err", res?.message);
            setLoading(false);
          }
        })
        .catch((err) => {
          console.log("err", err);
          setLoading(false);
          toast.error(err?.response?.data?.message);
        });
    }
  };

  const handleView = (id, index) => {
    const event = eventList[index];
    if (event && event.Event_Website) {
      // Open the event website in a new tab
      window.open(event.Event_Website, "_blank", "noopener,noreferrer");
    } else {
      toast.error("No website URL available for this event");
    }
  };

  const handleCreate = () => {
    navigate(`${path}/create`);
  };

  const handleDaysChange = (value) => {
    setCurrentDays(value);
    setCurrentPage(1); // Reset to first page when changing days
  };

  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    if (size !== pageSize) {
      setPageSize(size);
    }
  };

  return (
    <>
      {loading ? (
        <PageSnipper loading={loading} />
      ) : (
        <div className="px-6">
          <div className="flex justify-between mb-3">
            <div className="flex items-center gap-4">
              <p className="text-lg lg:text-xl font-medium">
                Events ({totalEvents})
              </p>
              <Select
                value={currentDays}
                onChange={handleDaysChange}
                options={dayOptions}
                style={{ width: 120 }}
                placeholder="Select days"
              />
            </div>
            <CustomButton
              icon={<CalendarPlus size={20} />}
              bgColor={"bg-blue-600"}
              content={"Create an Event"}
              clickHandler={handleCreate}
              additionalStyles={"text-white hover:bg-blue-700"}
            />
          </div>
          {eventList?.length > 0 ? (
            <>
              <div>
                <BaseTable
                  header={[
                    "Name",
                    "Event Organizer",
                    "Start Event",
                    "End Event",
                    "Actions",
                  ]}
                  data={eventList}
                  remove={handleDelete}
                  view={handleView}
                  type="event"
                />
              </div>
              <div className="flex justify-center mt-4">
                <Pagination
                  current={currentPage}
                  total={totalEvents}
                  pageSize={pageSize}
                  onChange={handlePageChange}
                  onShowSizeChange={handlePageChange}
                  showSizeChanger
                  showQuickJumper
                  showTotal={(total, range) =>
                    `${range[0]}-${range[1]} of ${total} events`
                  }
                  pageSizeOptions={["5", "10", "20", "50"]}
                />
              </div>
            </>
          ) : (
            <div className="w-full h-[60vh] flex justify-center flex-col items-center gap-8">
              <Frown size={48} color="#6a6868" strokeWidth={1.25} />
              <p className="text-lg text-gray-600 font-medium">
                No Events found
              </p>
            </div>
          )}

          <ToastContainer />
        </div>
      )}
    </>
  );
}

export default EventsHome;
